import { useMemo, useState } from 'react';
import type { ElementType } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Ban,
  Building2,
  Calendar,
  CheckCircle2,
  CircleDashed,
  DollarSign,
  Edit2,
  ExternalLink,
  FilePlus2,
  FileEdit,
  FileText,
  Hash,
  History,
  Package,
  PlusCircle,
  Trash2,
  Truck,
  Undo2,
  User,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { formatCurrency, formatDate, formatDateTime } from '@/lib/utils';
import {
  getQuoteHtmlUrlV4,
  QuoteStatusBadge,
  useQuote,
  useQuoteOperationsAuditLogs,
  useAddQuoteOperationsNote,
  useQuoteSkus,
  type QuoteLineItem,
  type QuoteOperationsAuditLog,
  type QuoteOperationsInternalNote,
  type QuoteOperationsMilestone,
  type QuoteSku,
} from '@/features/quotes';
import { useTasksByQuote } from '@/features/operations-tasks';
import { useUpdateQuotePriority } from '../hooks/useOperationsMetrics';
import { QuotePriorityBadge, type QuotePriority } from './QuotePriorityBadge';
import { ProjectContactSection } from '@/features/operations-projects/components/ProjectContactSection';
import { ProjectNotesSection } from '@/features/operations-projects/components/ProjectNotesSection';
import { ProjectTasksSection } from '@/features/operations-projects/components/ProjectTasksSection';
import { WorkflowStepper } from '@/features/operations-projects/components/WorkflowStepper';
import type { Project, ProjectSku, WorkflowStep } from '@/features/operations-projects/types';
import { CreateShippingLabelModal } from '@/features/planete-courrier';
import { CreateRmaFromQuoteModal } from '@/features/rma';
import { useHasAnyRole } from '@/hooks/useSession';
import { UNIVERSAL_STAGES, type ProjectKanbanCard } from '../types';

interface QuoteOperationsDetailPanelProps {
  quoteCard: ProjectKanbanCard | null;
  supportProject?: Project;
  supportProjectLoading?: boolean;
  onAddSupportProject: (quote: ProjectKanbanCard) => void;
  onEditSupportProject: () => void;
  onCreateInvoice?: () => void;
  onDiscard?: () => void;
  onDelete?: () => void;
  onCompleteStep: (stepKey: string) => void;
  onUncompleteStep: (stepKey: string) => void;
  isCompletingStep?: boolean;
}

function asNumber(value: string | number | null | undefined): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function buildQuoteOperationMilestones(
  supportProjectSteps: WorkflowStep[] | undefined,
  quoteSteps: QuoteOperationsMilestone[] | undefined,
  currentStage: ProjectKanbanCard['currentStage'] | string | undefined
): WorkflowStep[] {
  if (supportProjectSteps?.length) {
    // The stepper must reflect the quote's CURRENT kanban stage, not stale-ahead
    // milestone records — a quote moved back on the kanban must not keep later
    // stages shown as complete. Cap displayed completion to the current stage: a
    // step is shown complete only if it sits before the current stage among these
    // steps. Guard: only cap when the kanban stage maps to one of these steps
    // (custom milestone templates whose keys don't include currentStage are left
    // verbatim), and never for the terminal 'done' stage (all complete then).
    const sorted = [...supportProjectSteps].sort((a, b) => a.sortOrder - b.sortOrder);
    const stageIdx = sorted.findIndex((step) => step.key === currentStage);
    if (stageIdx < 0 || currentStage === 'done') return supportProjectSteps;
    return sorted.map((step, index) => {
      const completed = index < stageIdx;
      return {
        ...step,
        completed,
        status: completed ? ('completed' as const) : ('pending' as const),
        completedAt: completed ? step.completedAt : undefined,
      };
    });
  }

  const byKey = new Map((quoteSteps || []).filter((step) => step.key).map((step) => [step.key, step]));
  const currentIndex = UNIVERSAL_STAGES.findIndex((stage) => stage.key === currentStage);

  const steps = UNIVERSAL_STAGES.map((stage, index) => {
    const quoteStep = byKey.get(stage.key);
    return {
      id: quoteStep?.id || `${stage.key}-${index + 1}`,
      key: stage.key,
      label: quoteStep?.label || quoteStep?.displayName || stage.label,
      displayName: quoteStep?.displayName || quoteStep?.label || stage.label,
      sortOrder: quoteStep?.sortOrder || index + 1,
      status: quoteStep?.completed ? 'completed' as const : 'pending' as const,
      completed: Boolean(quoteStep?.completed),
      completedAt: quoteStep?.completedAt || undefined,
      completedBy: quoteStep?.completedBy || undefined,
      completedByName: quoteStep?.completedByName || undefined,
      notes: quoteStep?.notes || undefined,
    };
  });

  const hasCompletedStep = steps.some((step) => step.completed);
  if (!hasCompletedStep && currentIndex > 0) {
    return steps.map((step, index) => {
      const completed = index < currentIndex || currentStage === 'done';
      return {
        ...step,
        completed,
        status: completed ? 'completed' : 'pending',
      };
    });
  }

  return steps;
}

export function buildQuoteSkuRows(
  quoteSkus: QuoteSku[] | undefined,
  quoteLineItems: QuoteLineItem[] | undefined,
  supportProjectSkus: ProjectSku[] | undefined
): ProjectSku[] {
  if (quoteSkus?.length) {
    return quoteSkus.map((sku, index) => ({
      id: `${sku.sku || 'quote-sku'}-${index}`,
      sku: sku.sku || '',
      name: sku.name || sku.description || sku.sku || 'Line item',
      quantity: asNumber(sku.quantity),
      unitPrice: asNumber(sku.unitPrice),
      description: sku.description,
    }));
  }

  const lineItems = (quoteLineItems || []).filter((item) => !item.isNote);
  if (lineItems.length) {
    return lineItems.map((item, index) => ({
      id: item.id || `${item.sku || 'line'}-${index}`,
      sku: item.sku || '',
      name: item.name || item.description || item.sku || 'Line item',
      quantity: asNumber(item.quantity),
      unitPrice: asNumber(item.unitPrice),
      description: item.description || undefined,
    }));
  }

  return supportProjectSkus || [];
}

function DetailField({
  icon: Icon,
  label,
  value,
}: {
  icon?: ElementType;
  label: string;
  value: string | number | null | undefined;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        {Icon && <Icon className="h-4 w-4" />}
        {label}
      </div>
      <p className="font-medium break-words">{value || '-'}</p>
    </div>
  );
}

function DateField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Calendar className="h-4 w-4" />
        {label}
      </div>
      <p className="font-medium">{formatDate(value)}</p>
    </div>
  );
}

function formatTime(timeString?: string) {
  if (!timeString) return '';
  const [hours, minutes] = timeString.split(':');
  const hour = Number(hours);
  if (!Number.isFinite(hour)) return timeString;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  return `${hour % 12 || 12}:${minutes || '00'} ${ampm}`;
}

function LineItemsSection({
  skus,
  currency,
  isLoading,
}: {
  skus: ProjectSku[];
  currency: string;
  isLoading?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Package className="h-4 w-4" />
          SKUs
          {skus.length > 0 && <span className="text-muted-foreground">({skus.length})</span>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : skus.length === 0 ? (
          <p className="text-sm text-muted-foreground">No line items available</p>
        ) : (
          <div className="space-y-2">
            {skus.map((sku) => (
              <div
                key={sku.id}
                className="grid grid-cols-[1fr_auto] gap-3 rounded-md border bg-muted/30 p-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <code className="shrink-0 rounded bg-background px-1.5 py-0.5 font-mono text-xs">
                      {sku.sku || 'No SKU'}
                    </code>
                    <span className="truncate text-sm font-medium">{sku.name}</span>
                  </div>
                  {sku.description && (
                    <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                      {sku.description}
                    </p>
                  )}
                </div>
                <div className="text-right text-sm">
                  <div className="font-medium">Qty: {sku.quantity}</div>
                  {sku.unitPrice !== undefined && (
                    <div className="text-xs text-muted-foreground">
                      {formatCurrency(sku.unitPrice, currency)} each
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function getActivityDisplay(action: string): { icon: ElementType; color: string; label: string } {
  switch (action) {
    case 'operations_stage_advanced':
      return { icon: CheckCircle2, color: 'text-green-600', label: 'Stage advanced' };
    case 'operations_milestone_completed':
      return { icon: CheckCircle2, color: 'text-green-600', label: 'Milestone completed' };
    case 'operations_milestone_uncompleted':
      return { icon: CircleDashed, color: 'text-yellow-600', label: 'Milestone reopened' };
    case 'operations_notes_updated':
      return { icon: FileText, color: 'text-indigo-600', label: 'Notes updated' };
    case 'project_updated':
      return { icon: FileEdit, color: 'text-blue-600', label: 'Support project updated' };
    default:
      return { icon: History, color: 'text-muted-foreground', label: action };
  }
}

function formatActivityDetails(log: QuoteOperationsAuditLog): string | null {
  const details = log.details;
  if (!details) return null;
  if (typeof details.milestoneName === 'string') return details.milestoneName;
  if (typeof details.milestoneKey === 'string') return details.milestoneKey;
  if (typeof details.oldStage === 'string' && typeof details.newStage === 'string') {
    return `${details.oldStage} -> ${details.newStage}`;
  }
  if (typeof details.changeType === 'string') return `${details.changeType} notes`;
  return null;
}

/**
 * The mandatory "why" captured when a card is moved backward (stored in the
 * append-only audit log's details.reason). Surfaced so the regression and its
 * justification are visible together in the Activity Log.
 */
export function formatActivityReason(log: QuoteOperationsAuditLog): string | null {
  const details = log.details;
  if (details && typeof details.reason === 'string' && details.reason.trim()) {
    return details.reason.trim();
  }
  return null;
}

function QuoteActivityLog({ quoteId }: { quoteId?: string }) {
  const { data, isLoading } = useQuoteOperationsAuditLogs(quoteId, { pageLength: 20 });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <History className="h-4 w-4" />
          Activity Log
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((index) => (
              <Skeleton key={index} className="h-10 w-full" />
            ))}
          </div>
        ) : !data?.logs.length ? (
          <p className="text-sm text-muted-foreground py-2">No activity recorded yet</p>
        ) : (
          <ScrollArea className="h-[300px] pr-4">
            <div className="divide-y">
              {data.logs.map((log) => {
                const { icon: Icon, color, label } = getActivityDisplay(log.action);
                const details = formatActivityDetails(log);
                const reason = formatActivityReason(log);
                return (
                  <div key={log.id} className="flex gap-3 py-2">
                    <div className={`shrink-0 mt-0.5 ${color}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium">{label}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {formatDateTime(log.timestamp)}
                        </span>
                      </div>
                      {details && (
                        <p className="truncate text-sm text-muted-foreground">{details}</p>
                      )}
                      {reason && (
                        <p className="mt-0.5 text-sm text-foreground/80">
                          <span className="font-medium">Reason:</span> {reason}
                        </p>
                      )}
                      {log.performedByName && (
                        <p className="text-xs text-muted-foreground">by {log.performedByName}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}

// Internal operations notes — an append-only note log the ops team can add to,
// shown below the Activity Log in the quote detail panel.
export function QuoteOperationsNotesSection({ quoteId, notes }: { quoteId?: string; notes: QuoteOperationsInternalNote[] }) {
  const { t } = useTranslation('operations');
  const [draft, setDraft] = useState('');
  const addNote = useAddQuoteOperationsNote(quoteId);

  const handleAdd = () => {
    const content = draft.trim();
    if (!content || !quoteId || addNote.isPending) return;
    addNote.mutate(content, {
      onSuccess: () => {
        setDraft('');
        toast.success(t('quoteNotes.added', 'Note added'));
      },
      onError: (error) => {
        toast.error(t('quoteNotes.addFailed', 'Failed to add note'), {
          description: error instanceof Error ? error.message : undefined,
        });
      },
    });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="h-4 w-4" />
          {t('quoteNotes.title', 'Notes')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {notes.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('quoteNotes.empty', 'No notes yet.')}</p>
        ) : (
          notes.map((note, index) => (
            <div key={note.id ?? `${note.createdAt ?? 'note'}-${index}`} className="rounded-md border p-3">
              <p className="whitespace-pre-wrap text-sm">{note.content}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {note.createdByName || t('quoteNotes.unknownAuthor', 'Unknown')}
                {note.createdAt ? ` · ${formatDateTime(note.createdAt)}` : ''}
              </p>
            </div>
          ))
        )}
        {quoteId && (
          <div className="space-y-2">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={t('quoteNotes.placeholder', 'Add a note…')}
              aria-label={t('quoteNotes.placeholder', 'Add a note…')}
              rows={2}
            />
            <div className="flex justify-end">
              <Button size="sm" onClick={handleAdd} disabled={!draft.trim() || addNote.isPending}>
                <PlusCircle className="mr-1 h-4 w-4" />
                {t('quoteNotes.add', 'Add note')}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function QuoteOperationsDetailPanel({
  quoteCard,
  supportProject,
  supportProjectLoading,
  onAddSupportProject,
  onEditSupportProject,
  onCreateInvoice,
  onDiscard,
  onDelete,
  onCompleteStep,
  onUncompleteStep,
  isCompletingStep,
}: QuoteOperationsDetailPanelProps) {
  const { t } = useTranslation('operations');
  const quoteId = quoteCard?.quoteId || quoteCard?.id;
  const { data: quote, isLoading: quoteLoading } = useQuote(quoteId);
  const { data: quoteSkus, isLoading: quoteSkusLoading } = useQuoteSkus(quoteId, !!quoteId);
  const { data: tasksData, isLoading: tasksLoading } = useTasksByQuote(quoteId, { pageLength: 50 });
  const updatePriority = useUpdateQuotePriority();
  const canCreateShippingLabel = useHasAnyRole(['full_admin', 'director', 'operations_manager']);
  const [shippingLabelOpen, setShippingLabelOpen] = useState(false);
  const [rmaFromQuoteOpen, setRmaFromQuoteOpen] = useState(false);

  const milestones = useMemo(
    () => buildQuoteOperationMilestones(supportProject?.milestones, quote?.operationsMilestones, quoteCard?.currentStage),
    [quote?.operationsMilestones, quoteCard?.currentStage, supportProject?.milestones]
  );

  const skuRows = useMemo(
    () => buildQuoteSkuRows(quoteSkus, quote?.lineItems, supportProject?.skus),
    [quote?.lineItems, quoteSkus, supportProject?.skus]
  );

  if (!quoteCard) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        Select a quote to view details
      </div>
    );
  }

  const quoteNumber = quote?.quoteNumber || quoteCard.quoteNumber || quoteCard.projectNumber;
  const quoteTitle = quote?.name || quote?.projectName || quoteCard.quoteTitle || quoteCard.projectName || 'Untitled Quote';
  const accountName = quote?.accountName || quoteCard.accountName || '-';
  const currency = quote?.currency || quoteCard.currency || 'CAD';
  const total = quote?.total ?? quoteCard.amount;
  const notes = quote?.operationsNotes || supportProject?.notes || '';
  const hasSupportProject = Boolean(quoteCard.supportProjectId);
  const nextStep = milestones.find((step) => !step.completed);

  const openPdf = () => {
    if (quoteId) window.open(getQuoteHtmlUrlV4(quoteId), '_blank');
  };

  return (
    <div className="flex h-full flex-col">
      <div className="shrink-0 border-b bg-background px-6 pb-4 pt-6">
        {/* Stacked header: title/badges on the first row, action bar on its own
            full-width second row that wraps. The action bar must NOT be shrink-0
            in a justify-between row — that forces it to max-content and overflows
            the narrow panel (buttons clipped off the right edge). */}
        <div className="flex flex-col gap-4">
          <div className="min-w-0">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <h2 className="text-2xl font-semibold">Quote #{quoteNumber}</h2>
              {quote?.status && <QuoteStatusBadge status={quote.status} />}
              <Select
                value={quote?.priority ?? quoteCard.priority ?? undefined}
                onValueChange={(v) => {
                  if (quoteId) updatePriority.mutate({ quoteId, priority: v as QuotePriority });
                }}
                disabled={!quoteId || updatePriority.isPending}
              >
                <SelectTrigger className="h-7 w-[120px] text-xs" aria-label={t('quotePriority.label', 'Quote priority')}>
                  <SelectValue placeholder={t('quotePriority.set', 'Set priority')} />
                </SelectTrigger>
                <SelectContent>
                  {(['urgent', 'normal', 'low'] as const).map((p) => (
                    <SelectItem key={p} value={p}>
                      <QuotePriorityBadge priority={p} />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Badge variant={hasSupportProject ? 'secondary' : 'outline'}>
                {hasSupportProject ? 'Support project linked' : 'Needs support project'}
              </Badge>
            </div>
            <p className="truncate text-lg text-muted-foreground">{quoteTitle}</p>
            <p className="text-sm text-muted-foreground">{accountName}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {onCreateInvoice && (
              <Button size="sm" onClick={onCreateInvoice}>
                <FilePlus2 className="mr-1 h-4 w-4" />
                Create Invoice
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={openPdf} disabled={!quoteId}>
              <ExternalLink className="mr-1 h-4 w-4" />
              PDF
            </Button>
            {canCreateShippingLabel && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShippingLabelOpen(true)}
                disabled={!quoteId}
                title={t('planete-courrier:actions.createLabel', 'Create shipping label')}
              >
                <Truck className="mr-1 h-4 w-4" />
                {t('planete-courrier:actions.createLabel', 'Create shipping label')}
              </Button>
            )}
            {canCreateShippingLabel && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRmaFromQuoteOpen(true)}
                disabled={!quoteId}
                title={t('operations:rma.fromQuote.button', 'Create RMA')}
              >
                <Undo2 className="mr-1 h-4 w-4" />
                {t('operations:rma.fromQuote.button', 'Create RMA')}
              </Button>
            )}
            {hasSupportProject ? (
              <Button
                variant="outline"
                size="sm"
                onClick={onEditSupportProject}
                disabled={!supportProject || supportProjectLoading}
              >
                <Edit2 className="mr-1 h-4 w-4" />
                Edit Support
              </Button>
            ) : (
              <Button size="sm" onClick={() => onAddSupportProject(quoteCard)}>
                <PlusCircle className="mr-1 h-4 w-4" />
                Add Support
              </Button>
            )}
            {onDiscard && (
              <Button variant="outline" size="sm" onClick={onDiscard}>
                <Ban className="mr-1 h-4 w-4" />
                {t('discard.label', 'Discard')}
              </Button>
            )}
            {onDelete && (
              <Button variant="destructive" size="sm" onClick={onDelete}>
                <Trash2 className="mr-1 h-4 w-4" />
                {t('delete.label', 'Delete')}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="space-y-6">
          {quoteLoading ? (
            <Card>
              <CardContent className="p-6">
                <Skeleton className="mb-3 h-5 w-40" />
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Quote Info</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <DetailField icon={Hash} label="Quote Number" value={quoteNumber} />
                  <DetailField icon={Building2} label="Account" value={accountName} />
                  <DetailField icon={FileText} label="Project" value={quoteTitle} />
                  <DetailField icon={DollarSign} label="Value" value={formatCurrency(total, currency)} />
                  <DetailField icon={User} label="Account Manager" value={quote?.salesManagerName || quoteCard.accountManagerName} />
                  <DetailField icon={User} label="Contact" value={quote?.contractualManagerName || supportProject?.contact_name} />
                </div>
              </CardContent>
            </Card>
          )}

          <div>
            <h3 className="mb-3 text-sm font-medium text-muted-foreground">Workflow Progress</h3>
            <WorkflowStepper
              steps={milestones}
              canEdit={Boolean(supportProject)}
              onCompleteStep={supportProject ? onCompleteStep : undefined}
              onStepUncomplete={supportProject ? onUncompleteStep : undefined}
              isCompletingStep={isCompletingStep}
            />
            {nextStep && (
              <div className="mt-3 text-sm text-muted-foreground">
                Next step: <span className="font-medium text-foreground">{nextStep.displayName}</span>
              </div>
            )}
          </div>

          <Separator />

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Dates</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <DateField label="Created" value={quote?.createdDate || quote?.createdAt} />
                <DateField label="Sent" value={quote?.sentDate} />
                <DateField label="Accepted" value={quote?.acceptedDate} />
                <DateField label="Expiration" value={quote?.expirationDate} />
                <DateField label="Estimated Delivery" value={quote?.estimatedDelivery || supportProject?.scheduled_date} />
                <DateField label="Shipping" value={supportProject?.shipping_date} />
              </div>

              {supportProject?.scheduled_dates?.length ? (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">Scheduled Dates</p>
                  {supportProject.scheduled_dates.map((scheduledDate) => (
                    <div key={scheduledDate.id} className="flex flex-wrap items-center gap-2 text-sm">
                      {scheduledDate.status === 'confirmed' ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      ) : (
                        <CircleDashed className="h-4 w-4 text-yellow-600" />
                      )}
                      <span className="font-medium">{formatDate(scheduledDate.date)}</span>
                      {scheduledDate.time && (
                        <span className="text-muted-foreground">{formatTime(scheduledDate.time)}</span>
                      )}
                      <Badge variant="outline">{scheduledDate.status}</Badge>
                    </div>
                  ))}
                </div>
              ) : null}
            </CardContent>
          </Card>

          {!hasSupportProject && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <PlusCircle className="h-4 w-4" />
                  Supporting Project
                </CardTitle>
              </CardHeader>
              <CardContent className="flex items-center justify-between gap-4">
                <p className="text-sm text-muted-foreground">
                  Add a supporting project to manage scheduling, fulfillment notes, shipping, and assignees for this quote.
                </p>
                <Button size="sm" onClick={() => onAddSupportProject(quoteCard)}>
                  Add Support
                </Button>
              </CardContent>
            </Card>
          )}

          <ProjectContactSection
            contactName={supportProject?.contact_name || quote?.contractualManagerName || undefined}
            contactEmail={supportProject?.contact_email || quote?.contractualManagerEmail || undefined}
            contactPhone={supportProject?.contact_phone || quote?.contractualManagerPhone || undefined}
            accountManagerName={supportProject?.account_manager_name || quote?.salesManagerName || quoteCard.accountManagerName}
          />

          <ProjectNotesSection notes={notes} />

          <ProjectTasksSection
            tasks={tasksData?.tasks || []}
            isLoading={tasksLoading}
          />

          <LineItemsSection
            skus={skuRows}
            currency={currency}
            isLoading={quoteSkusLoading && skuRows.length === 0}
          />

          <QuoteActivityLog quoteId={quoteId} />

          <QuoteOperationsNotesSection quoteId={quoteId} notes={quote?.operationsInternalNotes ?? []} />
        </div>
      </div>

      <CreateShippingLabelModal
        open={shippingLabelOpen}
        onOpenChange={setShippingLabelOpen}
        quoteId={quoteId}
      />
      {rmaFromQuoteOpen && (
        <CreateRmaFromQuoteModal
          open={rmaFromQuoteOpen}
          onOpenChange={setRmaFromQuoteOpen}
          quoteId={quoteId}
          source="demo"
        />
      )}
    </div>
  );
}
